import type { DocumentStatus } from '@/lib/document-status';

import type { DossierPerson, DossierTimelineItem } from './types';
import { formatDateTime } from '@/lib/time';

// Derivación PURA del texto de cada evento del timeline (título + detalle +
// marcador visual). Cero enums crudos en la UI (UX-COPY-01). Mutation-testeable.

export interface TimelineView {
  title: string;
  detail: string;
  /** Marca del nodo: 'past' (histórico), 'red' (obsoleto/anulado/reproceso), '' (pendiente/actual). */
  marker: 'past' | 'red' | '';
}

const TRANSITION_LABELS: Record<DocumentStatus, string> = {
  reservado: 'Código reservado',
  borrador: 'Elaboración iniciada',
  en_revision: 'Enviado a revisión',
  en_aprobacion: 'Enviado a aprobación',
  vigente: 'Puesto en vigencia',
  obsoleto: 'Versión obsoleta',
  anulado: 'Reserva anulada',
};

/** Nombre a mostrar de un actor: "Sistema" si no hubo actor; "Usuario dado de baja"
 *  si el actor existe pero su nombre no resolvió (soft-delete). */
function actorLabel(person: DossierPerson | null): string {
  if (person === null) {
    return 'Sistema';
  }
  return person.displayName ?? 'Usuario dado de baja';
}

export function timelineItemView(item: DossierTimelineItem): TimelineView {
  const when = formatDateTime(item.at);

  if (item.kind === 'signature') {
    const who = item.signer.displayName ?? 'Usuario dado de baja';
    const ip = item.ip === null ? '' : ` · IP ${item.ip}`;
    return {
      title: `v${item.version} · Firmado y bloqueado`,
      detail: `${who} · ${when}${ip}`,
      marker: 'past',
    };
  }

  const action = item.isReproceso
    ? `Reproceso → ${TRANSITION_LABELS[item.toStatus]}`
    : TRANSITION_LABELS[item.toStatus];
  const reason = item.reason === null ? '' : ` · ${item.reason}`;
  const marker: TimelineView['marker'] =
    item.toStatus === 'obsoleto' || item.toStatus === 'anulado' || item.isReproceso
      ? 'red'
      : 'past';
  return {
    title: `v${item.version} · ${action}`,
    detail: `${actorLabel(item.actor)} · ${when}${reason}`,
    marker,
  };
}
